import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Paper,
  TextInput,
  Select,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Center,
  Loader,
  Divider,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconFilter,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useSurgeryCenterReportDeliveryAdmin,
  useCreateSurgeryCenterReportDelivery,
  useUpdateSurgeryCenterReportDelivery,
  useToggleSurgeryCenterReportDeliveryActive,
} from "../../../hooks/admin/useSurgeryCenterReportDelivery";
import { useSurgeryCentersLabsAdmin } from "../../../hooks/admin/useSurgeryCentersLabs";
import { useReportDeliveryMethodsAdmin } from "../../../hooks/admin/useReportDeliveryMethods";
import SurgeryCenterReportDeliveryForm, {
  SurgeryCenterReportDeliveryFormValues,
} from "../../../components/admin/surgerycenterreportdelivery/SurgeryCenterReportDeliveryForm";
import { SurgeryCenterReportDelivery } from "../../../services/admin/surgeryCenterReportDelivery.service";

interface DeliveryFilters {
  status: "all" | "active" | "inactive";
  surgeryCenterLabId?: string;
  methodId?: string;
}

const ITEMS_PER_PAGE = 20;

export default function SurgeryCenterReportDeliveryAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editing, setEditing] = useState<SurgeryCenterReportDelivery | null>(
    null
  );
  const [togglingItem, setTogglingItem] =
    useState<SurgeryCenterReportDelivery | null>(null);
  const [filters, setFilters] = useState<DeliveryFilters>({ status: "all" });
  const [pendingFilters, setPendingFilters] = useState<DeliveryFilters>({
    status: "all",
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);

  const {
    data: deliveries = [],
    isLoading,
    refetch,
  } = useSurgeryCenterReportDeliveryAdmin();
  const { data: surgeryCenters = [] } = useSurgeryCentersLabsAdmin();
  const { data: deliveryMethods = [] } = useReportDeliveryMethodsAdmin();
  const createDelivery = useCreateSurgeryCenterReportDelivery();
  const updateDelivery = useUpdateSurgeryCenterReportDelivery();
  const toggleActive = useToggleSurgeryCenterReportDeliveryActive();

  // Filter deliveries
  const filteredDeliveries = useMemo(() => {
    let result = deliveries;

    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (d) =>
          d.surgeryCenterLab?.name?.toLowerCase().includes(searchLower) ||
          d.reportDeliveryMethod?.name?.toLowerCase().includes(searchLower) ||
          d.email?.toLowerCase().includes(searchLower) ||
          d.phone?.includes(searchLower) ||
          d.fax?.includes(searchLower)
      );
    }

    if (filters.status !== "all") {
      result = result.filter((d) =>
        filters.status === "active" ? d.isActive : !d.isActive
      );
    }

    if (filters.surgeryCenterLabId) {
      result = result.filter(
        (d) => d.surgeryCenterLabId === filters.surgeryCenterLabId
      );
    }

    if (filters.methodId) {
      result = result.filter(
        (d) => d.reportDeliveryMethodId === filters.methodId
      );
    }

    return result;
  }, [deliveries, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredDeliveries.length / ITEMS_PER_PAGE);
  const paginatedDeliveries = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredDeliveries.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredDeliveries, page]);

  const hasActiveFilters =
    filters.status !== "all" || filters.surgeryCenterLabId || filters.methodId;

  // Filter handlers
  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setFilters({ status: "all" });
    setPendingFilters({ status: "all" });
    setPage(1);
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({ status: "all" });
  };

  const handleAdd = () => {
    setEditing(null);
    openFormModal();
  };

  const handleEdit = (item: SurgeryCenterReportDelivery) => {
    setEditing(item);
    openFormModal();
  };

  const handleSubmit = async (
    values: SurgeryCenterReportDeliveryFormValues
  ) => {
    if (editing) {
      await updateDelivery.mutateAsync({ id: editing.id, ...values });
    } else {
      await createDelivery.mutateAsync(values);
    }
    closeFormModal();
  };

  const handleToggleActive = (item: SurgeryCenterReportDelivery) => {
    setTogglingItem(item);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (!togglingItem) return;
    try {
      await toggleActive.mutateAsync({
        id: togglingItem.id,
        isActive: !togglingItem.isActive,
      });
      closeConfirmModal();
      setTogglingItem(null);
    } catch {
      closeConfirmModal();
      setTogglingItem(null);
    }
  };

  // Prepare dropdown options (sorted alphabetically)
  const surgeryCenterOptions = surgeryCenters
    .filter((s) => s.isActive)
    .map((s) => ({
      value: s.id,
      label: s.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const methodOptions = deliveryMethods
    .filter((m) => m.isActive)
    .map((m) => ({
      value: m.id,
      label: m.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Get labels for active filter badges
  const getSurgeryCenterLabel = (id: string) => {
    const center = surgeryCenters.find((s) => s.id === id);
    return center?.name || id;
  };

  const getMethodLabel = (id: string) => {
    const method = deliveryMethods.find((m) => m.id === id);
    return method?.name || id;
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Surgery Center Report Delivery</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage report delivery methods and contact information for
                surgery centers and labs
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add Delivery
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by surgery center, method, email, phone, or fax..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status !== "all" && (
                      <Badge variant="light" color="blue">
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    {filters.surgeryCenterLabId && (
                      <Badge variant="light" color="violet">
                        {getSurgeryCenterLabel(filters.surgeryCenterLabId)}
                      </Badge>
                    )}
                    {filters.methodId && (
                      <Badge variant="light" color="cyan">
                        {getMethodLabel(filters.methodId)}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredDeliveries.length} configuration
                  {filteredDeliveries.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Delivery Configurations ({filteredDeliveries.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredDeliveries.length
                      )}{" "}
                      of {filteredDeliveries.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredDeliveries.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {debouncedSearch || hasActiveFilters
                    ? "No configurations match the current filters"
                    : "No surgery center report delivery configurations found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Surgery Center / Lab</Table.Th>
                      <Table.Th>Delivery Method</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Phone</Table.Th>
                      <Table.Th>Fax</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedDeliveries.map((delivery) => (
                      <Table.Tr
                        key={delivery.id}
                        style={{ opacity: delivery.isActive ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {delivery.surgeryCenterLab?.name || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {delivery.reportDeliveryMethod?.name || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{delivery.email || "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{delivery.phone || "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{delivery.fax || "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={delivery.isActive ? "green" : "gray"}
                            variant="light"
                            size="sm"
                          >
                            {delivery.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(delivery)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={
                                delivery.isActive ? "Deactivate" : "Activate"
                              }
                            >
                              <ActionIcon
                                variant="subtle"
                                color={delivery.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(delivery)}
                              >
                                {delivery.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Modal */}
        <Modal.Root
          opened={formModalOpened}
          onClose={closeFormModal}
          size="md"
          centered
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {editing
                  ? "Edit Surgery Center Report Delivery"
                  : "Add New Surgery Center Report Delivery"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <SurgeryCenterReportDeliveryForm
                initialValues={editing || undefined}
                onSubmit={handleSubmit}
                surgeryCenters={surgeryCenterOptions}
                deliveryMethods={methodOptions}
              />
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                loading={createDelivery.isPending || updateDelivery.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editing ? "Update" : "Create"}
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Filter Modal */}
        <Modal.Root
          opened={filterModalOpened}
          onClose={closeFilterModal}
          size="md"
          centered
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Filter Delivery Configurations</Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <Stack gap="md">
                <Select
                  label="Status"
                  placeholder="Select status..."
                  data={[
                    { value: "all", label: "All" },
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                  value={pendingFilters.status}
                  onChange={(value) =>
                    setPendingFilters({
                      ...pendingFilters,
                      status: (value as "all" | "active" | "inactive") || "all",
                    })
                  }
                />
                <Select
                  label="Surgery Center / Lab"
                  placeholder="Select surgery center / lab..."
                  data={surgeryCenterOptions}
                  value={pendingFilters.surgeryCenterLabId || null}
                  onChange={(value) =>
                    setPendingFilters({
                      ...pendingFilters,
                      surgeryCenterLabId: value || undefined,
                    })
                  }
                  searchable
                  clearable
                />
                <Select
                  label="Delivery Method"
                  placeholder="Select delivery method..."
                  data={methodOptions}
                  value={pendingFilters.methodId || null}
                  onChange={(value) =>
                    setPendingFilters({
                      ...pendingFilters,
                      methodId: value || undefined,
                    })
                  }
                  clearable
                />
              </Stack>
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Group gap="xs">
                <Button variant="subtle" onClick={handleClearPendingFilters}>
                  Clear All
                </Button>
                <Button onClick={handleApplyFilters}>Apply Filters</Button>
              </Group>
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Confirmation Modal for Activate/Deactivate */}
        <Modal.Root
          opened={confirmModalOpened}
          onClose={() => {
            closeConfirmModal();
            setTogglingItem(null);
          }}
          size="sm"
          centered
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {togglingItem?.isActive
                  ? "Deactivate Delivery Configuration"
                  : "Activate Delivery Configuration"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
              <Text>
                Are you sure you want to{" "}
                <Text span fw={600}>
                  {togglingItem?.isActive ? "deactivate" : "activate"}
                </Text>{" "}
                the{" "}
                <Text span fw={600}>
                  {togglingItem?.reportDeliveryMethod?.name}
                </Text>{" "}
                delivery for{" "}
                <Text span fw={600}>
                  {togglingItem?.surgeryCenterLab?.name}
                </Text>
                ?
              </Text>
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button
                variant="default"
                onClick={() => {
                  closeConfirmModal();
                  setTogglingItem(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color={togglingItem?.isActive ? "red" : "green"}
                loading={toggleActive.isPending}
                onClick={confirmToggleActive}
              >
                {togglingItem?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>
      </Box>
    </Container>
  );
}
